import { ApiError, type ApiEnvelope } from './envelope'
import { getConfig } from '@/shared/config'
import { tokenStore } from '@/shared/auth/tokenStore'

/**
 * The single place HTTP happens.
 *
 * Three behaviours that matter, each driven by something the backend actually
 * does:
 *
 * 1. The body is parsed on non-2xx responses too. The API returns the envelope
 *    with a real message on 401/404/409; only reading it when `res.ok` would
 *    throw away every error message the user needs to see.
 * 2. A 401 triggers exactly one refresh attempt, shared across concurrent
 *    callers via a module-level promise. Ten requests failing at once must
 *    produce one refresh, not ten.
 * 3. Refresh itself never retries. If refreshing fails the session is gone,
 *    and looping would just spin.
 */

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  method?: Method
  body?: unknown
  /**
   * A multipart body, sent as is. The browser writes the Content-Type itself,
   * boundary included; setting it here would send a boundary that matches
   * nothing in the body and the server would read no files at all.
   */
  form?: FormData
  /** Set false for the auth endpoints, which must not attempt a refresh. */
  retryOnUnauthorized?: boolean
  signal?: AbortSignal
  /** What the caller expects back: the JSON envelope, or the raw bytes of a file. */
  accept?: string
}

/** Shared across callers so concurrent 401s collapse into one refresh. */
let refreshInFlight: Promise<boolean> | null = null

/** Notified when the session is definitively gone, so the UI can react once. */
type SessionExpiredListener = () => void
const sessionExpiredListeners = new Set<SessionExpiredListener>()

/** @returns an unsubscribe function, usable directly as a useEffect cleanup. */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener)
  return () => {
    // Braces matter: Set.delete returns a boolean, and an arrow function
    // returning it is not assignable to a useEffect destructor.
    sessionExpiredListeners.delete(listener)
  }
}

function notifySessionExpired() {
  sessionExpiredListeners.forEach((listener) => listener())
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const { apiBaseUrl } = getConfig()
  const headers: Record<string, string> = {
    Accept: options.accept ?? 'application/json',
    'Accept-Language': 'id-ID',
  }
  if (options.body !== undefined && !options.form) {
    headers['Content-Type'] = 'application/json'
  }
  const token = tokenStore.getAccessToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  return fetch(`${apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Always send cookies: the refresh token is an HttpOnly cookie scoped to
    // the auth endpoints.
    credentials: 'include',
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

/** One request, with the shared one-time refresh on a 401. */
async function fetchWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  const retryOnUnauthorized = options.retryOnUnauthorized ?? true
  let res = await rawFetch(path, options)
  if (res.status === 401 && retryOnUnauthorized) {
    const refreshed = await attemptRefresh()
    if (refreshed) {
      res = await rawFetch(path, options)
    } else {
      tokenStore.clear()
      notifySessionExpired()
    }
  }
  return res
}

async function parseEnvelope<T>(res: Response): Promise<ApiEnvelope<T>> {
  const text = await res.text()
  if (!text) {
    // An empty body on an error status still has to become a usable message.
    throw new ApiError(
      res.ok ? '00' : '07',
      res.ok ? '' : 'Tidak ada jawaban dari sistem. Coba ulangi sebentar lagi.',
      res.status,
      null,
      res.headers.get('X-Trace-Id'),
    )
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>
  } catch {
    throw new ApiError(
      '03',
      'Jawaban dari sistem tidak dapat dibaca. Coba ulangi sebentar lagi.',
      res.status,
      null,
      res.headers.get('X-Trace-Id'),
    )
  }
}

async function attemptRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await rawFetch('/auth/refresh', {
          method: 'POST',
          retryOnUnauthorized: false,
        })
        if (!res.ok) return false
        const envelope = await parseEnvelope<{ accessToken: string }>(res)
        if (envelope.resCd !== '00' || !envelope.resObj?.accessToken) return false
        tokenStore.setAccessToken(envelope.resObj.accessToken)
        return true
      } catch {
        return false
      } finally {
        // Cleared in a microtask so callers awaiting this promise all observe
        // the same result before a new attempt can start.
        queueMicrotask(() => {
          refreshInFlight = null
        })
      }
    })()
  }
  return refreshInFlight
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await fetchWithRefresh(path, options)
  const envelope = await parseEnvelope<T>(res)

  if (!res.ok || envelope.resCd !== '00') {
    throw new ApiError(
      envelope.resCd ?? '07',
      envelope.resMsg ?? 'Permintaan tidak dapat diselesaikan. Coba ulangi sebentar lagi.',
      res.status,
      envelope.fieldErrors,
      envelope.traceId ?? res.headers.get('X-Trace-Id'),
    )
  }

  return envelope.resObj as T
}

/**
 * A file, as bytes. A refusal still arrives as the JSON envelope, so its
 * message is read and thrown like any other error rather than handed back as
 * a "file" containing an error.
 */
async function requestBlob(path: string): Promise<Blob> {
  const res = await fetchWithRefresh(path, { method: 'GET', accept: '*/*' })
  if (!res.ok) {
    const envelope = await parseEnvelope<unknown>(res)
    throw new ApiError(
      envelope.resCd ?? '07',
      envelope.resMsg ?? 'File tidak dapat dibuka. Coba ulangi sebentar lagi.',
      res.status,
      envelope.fieldErrors,
      envelope.traceId ?? res.headers.get('X-Trace-Id'),
    )
  }
  return res.blob()
}

export const api = {
  /** Multipart: files and fields together, e.g. an upload with its category. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }),
  blob: (path: string) => requestBlob(path),
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { method: 'GET', signal } : { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
